import React, { useState, useEffect, useRef } from "react";
import { MapPin, Clock, Users, X, ChevronDown, ChevronUp } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_GRADIENT = "linear-gradient(180deg, #0C1A19 0%, #080C0C 20%, #080C0C 80%, #040808 100%)";

const parseRichText = (text) => {
  if (!text) return "";
  return text
    .replace(/^## (.+)$/gm, '<span style="color:inherit;font-size:15px;font-weight:700;display:block;margin:10px 0 2px">$1</span>')
    .replace(/^### (.+)$/gm, '<span style="color:inherit;font-size:13px;font-weight:600;display:block;margin:6px 0 2px">$1</span>')
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color:inherit;font-weight:700;font-family:inherit">$1</strong>')
    .replace(/__(.*?)__/g, '<u style="color:inherit;text-decoration:underline">$1</u>')
    .replace(/\*(.*?)\*/g, '<em style="color:inherit;font-style:italic">$1</em>')
    .replace(/^- (.+)$/gm, '<li style="color:inherit;margin-bottom:4px">$1</li>')
    .replace(/((?:<li[^>]*>.*?<\/li>)\n?)+/g, m => '<ul style="margin:6px 0 8px 20px;padding:0;list-style:disc">' + m.replace(/\n/g, '') + '</ul>')
    .replace(/\n/g, "<br>");
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  boxSizing: "border-box",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.09)",
  borderRadius: 6,
  color: "#fff",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 300,
  fontSize: 13,
  outline: "none"
};

const labelStyle = {
  display: "block",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  fontSize: 12,
  color: "rgba(255,255,255,0.45)",
  marginBottom: 6,
  letterSpacing: 0.3
};

gsap.registerPlugin(ScrollTrigger);

const Careers = () => {
  const heroHeadingRef = useRef(null);
  const jobListRef = useRef(null);
  const expandedBodyRefs = useRef({});
  const prevExpandedJobRef = useRef(null);

  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [applicationData, setApplicationData] = useState({
    name: "", email: "", phone: "", resume: null, coverLetter: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/jobs`)
      .then(res => { if (res.data.success) setJobs(res.data.data); })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  // Hero heading mount animation
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !heroHeadingRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from(heroHeadingRef.current, {
        clipPath: "inset(0 100% 0 0)", duration: 0.9, ease: "power3.out",
      });
    });
    return () => ctx.revert();
  }, []);

  // Job cards stagger on load
  useEffect(() => {
    if (isLoading || !jobListRef.current) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const cards = jobListRef.current.children;
    if (!cards.length) return;

    const ctx = gsap.context(() => {
      gsap.from(cards, {
        y: 20, opacity: 0,
        stagger: 0.08, duration: 0.55, ease: "power2.out",
        scrollTrigger: { trigger: jobListRef.current, start: "top 85%", once: true },
      });
    });

    return () => ctx.revert();
  }, [isLoading]);

  // GSAP accordion animation
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Close the previously open card
    if (prevExpandedJobRef.current && prevExpandedJobRef.current !== expandedJob) {
      const prevEl = expandedBodyRefs.current[prevExpandedJobRef.current];
      if (prevEl) {
        prefersReducedMotion
          ? gsap.set(prevEl, { height: 0, opacity: 0 })
          : gsap.to(prevEl, { height: 0, opacity: 0, duration: 0.3, ease: "power2.in" });
      }
    }

    // Open the current card
    if (expandedJob && expandedBodyRefs.current[expandedJob]) {
      const el = expandedBodyRefs.current[expandedJob];
      gsap.set(el, { height: 0, opacity: 0, overflow: "hidden" });
      prefersReducedMotion
        ? gsap.set(el, { height: "auto", opacity: 1 })
        : gsap.to(el, { height: "auto", opacity: 1, duration: 0.35, ease: "power2.out" });
    }

    prevExpandedJobRef.current = expandedJob;
  }, [expandedJob]);

  const handleInputChange = (e) => {
    setApplicationData({ ...applicationData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setApplicationData({ ...applicationData, resume: e.target.files[0] });
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("jobId", selectedJob.id.toString());
      formData.append("jobTitle", selectedJob.title);
      formData.append("name", applicationData.name);
      formData.append("email", applicationData.email);
      if (applicationData.phone) formData.append("phone", applicationData.phone);
      if (applicationData.coverLetter) formData.append("coverLetter", applicationData.coverLetter);
      formData.append("resume", applicationData.resume);

      const response = await axios.post(`${API}/jobs/apply`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setIsSubmitted(true);
        setApplicationData({ name: "", email: "", phone: "", resume: null, coverLetter: "" });
      } else {
        setError("Failed to submit application. Please try again.");
      }
    } catch (err) {
      if (err.response?.data?.detail) setError(err.response.data.detail);
      else setError("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openJobModal = (job) => {
    setSelectedJob(job);
    setIsSubmitted(false);
  };

  const closeJobModal = () => {
    setSelectedJob(null);
    setError(null);
    setApplicationData({ name: "", email: "", phone: "", resume: null, coverLetter: "" });
  };

  return (
    <div style={{ background: PAGE_GRADIENT, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      {/* Hero */}
      <section style={{
        paddingTop: 128,
        paddingBottom: 72,
        paddingLeft: "clamp(24px, 6vw, 80px)",
        paddingRight: "clamp(24px, 6vw, 80px)"
      }}>
        <h1 ref={heroHeadingRef} style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: "clamp(36px, 5vw, 64px)",
          color: "#fff",
          letterSpacing: -2,
          lineHeight: 1.1,
          marginBottom: 20
        }}>
          Join the <span style={{ color: "#7BB5B1" }}>team</span>
        </h1>
        <p style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: 15,
          color: "rgba(255,255,255,0.45)",
          maxWidth: 520,
          lineHeight: 1.8
        }}>
          Join a team of passionate innovators dedicated to creating breakthrough AI solutions that shape the future.
        </p>
      </section>

      {/* Job listings — flex:1 pushes footer to bottom when no jobs are listed */}
      <section style={{
        flex: 1,
        paddingLeft: "clamp(24px, 5vw, 80px)",
        paddingRight: "clamp(24px, 5vw, 80px)",
        paddingBottom: 120,
        paddingTop: 64,
        borderTop: "1px solid rgba(255,255,255,0.04)"
      }}>
        {isLoading ? (
          <p style={{
            color: "rgba(255,255,255,0.25)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: 14
          }}>
            Loading positions...
          </p>
        ) : jobs.length === 0 ? (
          <p style={{
            color: "rgba(255,255,255,0.25)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: 15,
            textAlign: "center",
            paddingTop: 40
          }}>
            No open positions right now. Check back soon.
          </p>
        ) : (
          <div ref={jobListRef} style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
            {jobs.map(job => {
              const isExpanded = expandedJob === job.id;
              const isOtherExpanded = expandedJob !== null && expandedJob !== job.id;
              const isActive = job.jobStatus === "Active";
              return (
                <div
                  key={job.id}
                  style={{
                    background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
                    border: "1px solid",
                    borderColor: isExpanded ? "rgba(91,148,144,0.55)" : "rgba(91,148,144,0.22)",
                    borderRadius: 10,
                    overflow: "hidden",
                    width: "100%",
                    boxSizing: "border-box",
                    transition: "all 0.25s ease",
                    opacity: isOtherExpanded ? 0.58 : 1,
                    boxShadow: isExpanded
                      ? "0 0 36px rgba(91,148,144,0.26), 0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
                      : "0 0 22px rgba(91,148,144,0.13), 0 4px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}
                >
                  {/* Card header */}
                  <div
                    className="careers-job-header"
                    onClick={() => isActive && setExpandedJob(isExpanded ? null : job.id)}
                    style={{
                      padding: "28px 32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minHeight: 88,
                      cursor: isActive ? "pointer" : "default"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        fontSize: 15,
                        color: "#fff",
                        letterSpacing: -0.3
                      }}>
                        {job.title}
                      </span>
                      <span style={{
                        padding: "3px 10px",
                        background: "rgba(45,92,90,0.15)",
                        border: "1px solid rgba(91,148,144,0.25)",
                        borderRadius: 99,
                        fontSize: 11,
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        color: "#7BB5B1",
                        letterSpacing: 0.5
                      }}>
                        {job.type}
                      </span>
                      {job.jobStatus === "Upcoming" && (
                        <span style={{
                          padding: "3px 10px",
                          background: "rgba(255,193,7,0.1)",
                          border: "1px solid rgba(255,193,7,0.25)",
                          borderRadius: 99,
                          fontSize: 11,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                          color: "#FFC107"
                        }}>
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div className="careers-job-meta" style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <span style={{
                          display: "flex", alignItems: "center", gap: 4,
                          fontSize: 12, color: "rgba(255,255,255,0.5)",
                          fontFamily: "Montserrat, sans-serif", fontWeight: 300
                        }}>
                          <MapPin size={12} />{job.location}
                        </span>
                        <span style={{
                          display: "flex", alignItems: "center", gap: 4,
                          fontSize: 12, color: "rgba(255,255,255,0.35)",
                          fontFamily: "Montserrat, sans-serif", fontWeight: 300
                        }}>
                          <Users size={12} />{job.department}
                        </span>
                        {job.salary && (
                          <span style={{
                            padding: "2px 10px",
                            background: "rgba(45,92,90,0.12)",
                            border: "1px solid rgba(91,148,144,0.2)",
                            borderRadius: 99,
                            fontSize: 11,
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 500,
                            color: "#7BB5B1"
                          }}>
                            {job.salary}
                          </span>
                        )}
                        {job.experience && (
                          <span style={{
                            display: "flex", alignItems: "center", gap: 4,
                            fontSize: 12, color: "rgba(255,255,255,0.45)",
                            fontFamily: "Montserrat, sans-serif", fontWeight: 300
                          }}>
                            <Clock size={12} />{job.experience}
                          </span>
                        )}
                      </div>
                      {isActive && (
                        <div style={{ color: "rgba(255,255,255,0.3)" }}>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded body — always rendered; GSAP controls height */}
                  <div
                    ref={el => { if (el) expandedBodyRefs.current[job.id] = el; }}
                    style={{ height: 0, overflow: "hidden", opacity: 0 }}
                  >
                    <div className="careers-job-body-inner" style={{
                      padding: "0 24px 24px",
                      borderTop: "1px solid rgba(255,255,255,0.04)"
                    }}>
                      <div
                        style={{
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 300,
                          fontSize: 13,
                          color: "#e8e8e8",
                          lineHeight: 1.9,
                          marginTop: 20,
                          marginBottom: 16
                        }}
                        dangerouslySetInnerHTML={{ __html: parseRichText(job.fullDescription || job.description) }}
                      />
                      {job.requirements && job.requirements.length > 0 && (
                        <>
                          <p style={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 500,
                            fontSize: 11,
                            color: "rgba(255,255,255,0.4)",
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            marginBottom: 10
                          }}>
                            Requirements
                          </p>
                          <ul style={{ margin: "0 0 20px 0", padding: "0 0 0 18px" }}>
                            {job.requirements.map((req, i) => (
                              <li key={i} style={{
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 300,
                                fontSize: 13,
                                color: "#e0e0e0",
                                lineHeight: 1.8,
                                marginBottom: 6
                              }}>
                                {req}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{
                          display: "flex", alignItems: "center", gap: 4,
                          fontSize: 12, color: "rgba(255,255,255,0.5)",
                          fontFamily: "Montserrat, sans-serif", fontWeight: 300
                        }}>
                          <Clock size={12} />{job.experience}
                        </span>
                        <button
                          onClick={() => openJobModal(job)}
                          style={{
                            padding: "8px 20px",
                            background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
                            border: "1px solid rgba(91,148,144,0.52)",
                            borderRadius: 6,
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 500,
                            fontSize: 13,
                            color: "#fff",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            boxShadow: "0 0 18px rgba(91,148,144,0.28), 0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 0 28px rgba(91,148,144,0.45), 0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.16)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 0 18px rgba(91,148,144,0.28), 0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)";
                          }}
                        >
                          Apply Now →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Application Modal */}
      {selectedJob && (
        <div
          onClick={(e) => e.target === e.currentTarget && closeJobModal()}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            backdropFilter: "blur(4px)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24
          }}
        >
          <div style={{
            background: "rgba(13,31,30,0.98)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            padding: "clamp(20px, 4vw, 40px)",
            width: "100%",
            maxWidth: 480,
            maxHeight: "90vh",
            overflowY: "auto"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 32
            }}>
              <div>
                <h2 style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                  fontSize: 18,
                  color: "#fff",
                  marginBottom: 4
                }}>
                  Apply for {selectedJob.title}
                </h2>
                <p style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 300,
                  fontSize: 13,
                  color: "rgba(255,255,255,0.35)"
                }}>
                  {selectedJob.department} · {selectedJob.location}
                </p>
              </div>
              <button
                onClick={closeJobModal}
                style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)", padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            {isSubmitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{
                  width: 48, height: 48,
                  background: "rgba(45,92,90,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px"
                }}>
                  <span style={{ color: "#7BB5B1", fontSize: 20 }}>✓</span>
                </div>
                <p style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                  fontSize: 15,
                  color: "#fff",
                  marginBottom: 8
                }}>
                  Application submitted!
                </p>
                <p style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 300,
                  fontSize: 13,
                  color: "rgba(255,255,255,0.4)"
                }}>
                  We'll be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleApply} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input
                    name="name"
                    value={applicationData.name}
                    onChange={handleInputChange}
                    required
                    style={inputStyle}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    value={applicationData.email}
                    onChange={handleInputChange}
                    required
                    style={inputStyle}
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone (optional)</label>
                  <input
                    name="phone"
                    value={applicationData.phone}
                    onChange={handleInputChange}
                    style={inputStyle}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Resume *</label>
                  <input
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    required
                    style={{ ...inputStyle, padding: "8px 14px", color: "rgba(255,255,255,0.5)" }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Cover Letter (optional)</label>
                  <textarea
                    name="coverLetter"
                    value={applicationData.coverLetter}
                    onChange={handleInputChange}
                    style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                    placeholder="Tell us about yourself..."
                  />
                </div>
                {error && (
                  <p style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 300,
                    fontSize: 13,
                    color: "#f87171"
                  }}>
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting || !applicationData.resume}
                  style={{
                    width: "100%",
                    padding: 12,
                    background: isSubmitting ? "rgba(45,92,90,0.5)" : "#2D5C5A",
                    border: "none",
                    borderRadius: 6,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    fontSize: 14,
                    color: "#fff",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    transition: "background 0.2s"
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Careers;
